import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Download, Smartphone, Share, Home } from 'lucide-react';

// Admin-only Install Prompt - shows only in back office after login
export default function AdminInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    // If already installed, mark it and don't show prompt
    if (standalone) {
      localStorage.setItem('adminAppInstalled', 'true');
      return;
    }
    
    // Check if already added to home screen (user confirmed)
    const alreadyInstalled = localStorage.getItem('adminAppInstalled') === 'true';
    if (alreadyInstalled) {
      return;
    }
    
    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // For iOS, show prompt after a short delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 2000);
    }
    
    // For desktop Chrome that supports PWA
    if (!iOS && 'serviceWorker' in navigator) {
      setTimeout(() => {
        // If no beforeinstallprompt fired but PWA is supported, show generic prompt
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 3000);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User install choice:', outcome);
      if (outcome === 'accepted') {
        localStorage.setItem('adminAppInstalled', 'true');
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleAddedToHomeScreen = () => {
    // User confirmed they added to home screen
    localStorage.setItem('adminAppInstalled', 'true');
    setShowPrompt(false);
  };

  const handleLater = () => {
    setShowPrompt(false);
    // Don't set localStorage - will show again next session
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96 animate-in slide-in-from-bottom-4">
      <Card className="p-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl border-0">
        <button 
          onClick={handleLater}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Home className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">Add to Home Screen</h3>
            <p className="text-sm text-white/90 mt-1">
              {isIOS 
                ? 'Add Tour Finder to your Home Screen for instant access and push notifications.'
                : 'Install the admin app for quick access and real-time booking alerts.'}
            </p>
            
            {isIOS ? (
              <div className="mt-3">
                <div className="p-2 bg-white/10 rounded-lg text-sm mb-3 space-y-2">
                  <p className="flex items-center gap-2 font-medium">
                    <span className="bg-white/20 rounded px-1.5 py-0.5">1</span>
                    Tap the <Share className="h-4 w-4 inline mx-1" /> Share button below
                  </p>
                  <p className="flex items-center gap-2 font-medium">
                    <span className="bg-white/20 rounded px-1.5 py-0.5">2</span>
                    Scroll down and tap <strong>"Add to Home Screen"</strong>
                  </p>
                  <p className="flex items-center gap-2 font-medium">
                    <span className="bg-white/20 rounded px-1.5 py-0.5">3</span>
                    Tap <strong>"Add"</strong> in the top right
                  </p>
                </div>
                <p className="text-xs text-white/80 mb-2">
                  After adding, open the app from your Home Screen to receive push notifications!
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAddedToHomeScreen}
                    className="flex-1 bg-white text-green-700 hover:bg-white/90 gap-2"
                  >
                    <Smartphone className="h-4 w-4" />
                    I've Added It
                  </Button>
                  <Button 
                    onClick={handleLater}
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                  >
                    Later
                  </Button>
                </div>
              </div>
            ) : deferredPrompt ? (
              <div className="flex gap-2 mt-3">
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-white text-green-700 hover:bg-white/90 gap-2"
                >
                  <Download className="h-4 w-4" />
                  Install App
                </Button>
                <Button 
                  onClick={handleLater}
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                >
                  Later
                </Button>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-xs text-white/80 mb-2">
                  Use your browser menu to "Add to Home Screen" or "Install App"
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAddedToHomeScreen}
                    className="flex-1 bg-white text-green-700 hover:bg-white/90 gap-2"
                  >
                    <Smartphone className="h-4 w-4" />
                    I've Added It
                  </Button>
                  <Button 
                    onClick={handleLater}
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                  >
                    Later
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
